import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, Mic, Pause, Play, Sparkles } from "lucide-react";
import { ModuleIntroModal } from "../../module/ModuleIntroModal";
import { GymCountdownOverlay, type GymCountdownPhase } from "../../gym/GymCountdownOverlay";
import { SongLearnCanvasBackdrop } from "../../SongLearnCanvasBackdrop";
import { PracticeSurface } from "../../practice/PracticeSurface";
import { Button } from "../../ui/button";
import { useGuitarSynth } from "../../../hooks/useGuitarSynth";
import { usePitchDetector } from "../../../hooks/usePitchDetector";
import { useInstrument } from "../../../contexts/InstrumentContext";
import type { ScalePathMission } from "../../../lib/scales/scalePathPlan";
import {
  buildScalePathMissionRuntime,
  pitchClassMatches,
} from "../../../lib/scales/scaleLearnEngine";
import type { NoteName } from "../../../lib/guitarEngine";
import { BEGINNER_PATH_STUDIO_NECK_CLASS } from "../../../lib/beginnerPath/beginnerPathLayout";
import { BEGINNER_PATH_THEME as PATH } from "../../../lib/beginnerPath/beginnerPathTheme";
import { StudioTunerControl } from "../../StudioTunerControl";

const COUNTDOWN_TICK_MS = 880;
const COUNTDOWN_GO_MS = 1_000;

type StudioPhase = "intro" | "countdown" | "ready";
type StudioMode = "learn" | "practice";

export interface ScalePathMissionStudioProps {
  mission: ScalePathMission;
  wasCompletedBefore: boolean;
  pathMode?: boolean;
  onBack: () => void;
  onComplete: () => void;
}

export function ScalePathMissionStudio({
  mission,
  wasCompletedBefore,
  pathMode = false,
  onBack,
  onComplete,
}: ScalePathMissionStudioProps) {
  const { surface } = useInstrument();
  const { playFret } = useGuitarSynth();
  const runtime = useMemo(() => buildScalePathMissionRuntime(mission), [mission]);

  const [phase, setPhase] = useState<StudioPhase>("intro");
  const [countdown, setCountdown] = useState<GymCountdownPhase | null>(null);
  const [mode, setMode] = useState<StudioMode>("learn");
  const [isPlaying, setIsPlaying] = useState(false);
  const [seqIndex, setSeqIndex] = useState(0);
  const [learnDone, setLearnDone] = useState(false);
  const [feedback, setFeedback] = useState<"success" | "fail" | null>(null);
  const [practiceActive, setPracticeActive] = useState(false);

  const sequence = runtime.sequence;
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const seqIndexRef = useRef(0);
  const sequenceRef = useRef(sequence);
  const practiceActiveRef = useRef(false);

  useEffect(() => {
    sequenceRef.current = sequence;
  }, [sequence]);
  useEffect(() => {
    seqIndexRef.current = seqIndex;
  }, [seqIndex]);
  useEffect(() => {
    practiceActiveRef.current = practiceActive;
  }, [practiceActive]);

  const stopLearnPlayback = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setIsPlaying(false);
  }, []);

  const startLearnPlayback = useCallback(() => {
    if (sequence.length === 0) return;
    stopLearnPlayback();
    setLearnDone(false);
    setSeqIndex(0);
    seqIndexRef.current = 0;
    const beatMs = (60 / mission.bpm) * 1000;
    let idx = 0;
    playFret(sequence[0].string, sequence[0].fret, beatMs * 0.85);
    setIsPlaying(true);

    intervalRef.current = setInterval(() => {
      idx += 1;
      if (idx >= sequence.length) {
        stopLearnPlayback();
        setLearnDone(true);
        setSeqIndex(0);
        return;
      }
      setSeqIndex(idx);
      seqIndexRef.current = idx;
      const pos = sequence[idx];
      playFret(pos.string, pos.fret, beatMs * 0.85);
    }, beatMs);
  }, [sequence, mission.bpm, playFret, stopLearnPlayback]);

  useEffect(() => () => stopLearnPlayback(), [stopLearnPlayback]);

  const handleNoteDetected = useCallback(
    (note: NoteName) => {
      if (!practiceActiveRef.current) return;
      const idx = seqIndexRef.current;
      const target = sequenceRef.current[idx]?.note;
      if (!target) return;

      if (pitchClassMatches(target, note)) {
        setFeedback("success");
        const next = idx + 1;
        if (next >= sequenceRef.current.length) {
          setPracticeActive(false);
          practiceActiveRef.current = false;
          setSeqIndex(0);
          stopListeningRef.current?.();
          window.setTimeout(() => onComplete(), 600);
        } else {
          setSeqIndex(next);
          seqIndexRef.current = next;
        }
      } else {
        setFeedback("fail");
      }
      window.setTimeout(() => setFeedback(null), 500);
    },
    [onComplete]
  );

  const stopListeningRef = useRef<(() => void) | null>(null);
  const { startListening, stopListening, state: pitchState } = usePitchDetector({
    threshold: 0.003,
    holdTimeMs: 150,
    onNoteDetected: handleNoteDetected,
  });
  stopListeningRef.current = stopListening;

  const startPractice = useCallback(async () => {
    setMode("practice");
    setSeqIndex(0);
    seqIndexRef.current = 0;
    setPracticeActive(true);
    practiceActiveRef.current = true;
    stopLearnPlayback();
    await startListening();
  }, [startListening, stopLearnPlayback]);

  const stopPractice = useCallback(() => {
    setPracticeActive(false);
    practiceActiveRef.current = false;
    stopListening();
  }, [stopListening]);

  const handleBeginCountdown = useCallback(() => {
    setCountdown(3);
  }, []);

  useEffect(() => {
    if (countdown === null) return;
    if (countdown === "go") {
      const id = window.setTimeout(() => {
        setPhase("ready");
        setCountdown(null);
      }, COUNTDOWN_GO_MS);
      return () => window.clearTimeout(id);
    }
    const id = window.setTimeout(() => {
      if (typeof countdown === "number") {
        if (countdown <= 1) setCountdown("go");
        else setCountdown(countdown - 1);
      }
    }, COUNTDOWN_TICK_MS);
    return () => window.clearTimeout(id);
  }, [countdown]);

  const highlightedPositions = useMemo(() => {
    const staticBox = runtime.patternNotes;
    if (mode === "learn" && isPlaying && sequence[seqIndex]) {
      const active = sequence[seqIndex];
      const key = `${active.string}-${active.fret}`;
      return staticBox.map((p) =>
        `${p.string}-${p.fret}` === key
          ? { ...p, highlight: "active" as const }
          : p
      );
    }
    if (mode === "practice" && practiceActive && sequence[seqIndex]) {
      const active = sequence[seqIndex];
      const key = `${active.string}-${active.fret}`;
      return staticBox.map((p) =>
        `${p.string}-${p.fret}` === key
          ? { ...p, highlight: "active" as const }
          : p
      );
    }
    return staticBox;
  }, [
    runtime.patternNotes,
    mode,
    isPlaying,
    practiceActive,
    sequence,
    seqIndex,
  ]);

  const dimNeck = phase === "intro" || countdown !== null;
  const accent = "#c4b5fd";

  if (surface === "keyboard") {
    return (
      <div className="flex flex-1 flex-col items-center justify-center p-8 text-center">
        <p className="text-sm text-white/60 max-w-md">
          Estas misiones usan cajas 3NPS en guitarra. Elige guitarra en el selector de instrumento.
        </p>
        <Button variant="outline" className="mt-4" onClick={onBack}>
          Volver
        </Button>
      </div>
    );
  }

  if (sequence.length === 0) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center p-8 text-center">
        <p className="text-sm text-white/60">No se pudo generar la secuencia para esta misión.</p>
        <Button variant="outline" className="mt-4" onClick={onBack}>
          Volver
        </Button>
      </div>
    );
  }

  return (
    <SongLearnCanvasBackdrop ambient="lite" className="flex min-h-0 flex-1 flex-col w-full">
      <div className={`flex h-full min-h-0 flex-col ${pathMode ? "w-full max-w-[min(100%,1600px)] mx-auto" : ""}`}>
        <header
          className="mx-2 mt-2 flex shrink-0 items-center justify-between gap-3 rounded-2xl border border-white/[0.08] bg-black/20 px-4 py-3 backdrop-blur-sm sm:mx-4"
        >
          <div className="flex min-w-0 items-center gap-3">
            <button
              type="button"
              onClick={onBack}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border transition-colors hover:bg-white/5"
              style={{ borderColor: PATH.cardBorder, color: PATH.textMuted }}
              aria-label="Volver a misiones"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <div className="min-w-0">
              {pathMode && (
                <p className="mb-0.5 text-[10px] uppercase tracking-[0.14em] text-[#c4b5fd]/70">
                  Camino principiante
                </p>
              )}
              <h1 className="truncate font-serif text-sm font-light sm:text-base">{mission.title}</h1>
              <p className="truncate text-[11px] text-white/45">
                {runtime.positionLabel} · {sequence.length} notas
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <StudioTunerControl compact />
            <div className="flex shrink-0 gap-1 rounded-xl border border-white/10 p-0.5">
              {(["learn", "practice"] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => {
                    if (m === mode) return;
                    stopLearnPlayback();
                    stopPractice();
                    setMode(m);
                  }}
                  className="rounded-lg px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider transition-colors"
                  style={{
                    background: mode === m ? "rgba(196, 181, 253, 0.2)" : "transparent",
                    color: mode === m ? "#e9e0ff" : "rgba(255,255,255,0.45)",
                  }}
                >
                  {m === "learn" ? "Aprender" : "Practicar"}
                </button>
              ))}
            </div>
          </div>
        </header>

        <div className="relative mx-2 mb-2 mt-2 flex min-h-[min(58vh,520px)] flex-1 flex-col overflow-hidden rounded-2xl sm:mx-4">
          <div
            className={`flex flex-1 items-center justify-center p-2 transition-opacity duration-500 ${dimNeck ? "opacity-35" : "opacity-100"}`}
          >
            <div
              className={`relative flex h-full w-full items-center justify-center ${BEGINNER_PATH_STUDIO_NECK_CLASS}`}
              style={{
                transform: "scale(1.15)",
                transformOrigin: "center center",
              }}
            >
              <PracticeSurface
                className="h-full w-full"
                highlightedPositions={highlightedPositions}
                showNoteNames={mode === "learn" && learnDone}
                showDegrees
                visualStyle="lutherie"
                practiceFeedback={feedback === "fail" ? "fail" : feedback === "success" ? "success" : undefined}
              />
            </div>
          </div>

          {phase === "intro" && (
            <ModuleIntroModal
              title={mission.title}
              subtitle={`${mission.subtitle} · ${runtime.positionLabel}`}
              description={mission.description}
              instructions={mission.instructions}
              tags={mission.tags}
              timeEstimateMin={mission.timeEstimateMin}
              accent={accent}
              accentSoft="rgba(196, 181, 253, 0.12)"
              accentGlow="rgba(196, 181, 253, 0.22)"
              borderHover={PATH.cardBorderLavender}
              textMain={PATH.text}
              textMuted={PATH.textMuted}
              textDim={PATH.textDim}
              wasCompletedBefore={wasCompletedBefore}
              onBegin={handleBeginCountdown}
            />
          )}
          {countdown !== null && <GymCountdownOverlay phase={countdown} />}

          {phase === "ready" && (
            <div className="absolute bottom-4 left-1/2 z-30 w-[min(92%,480px)] -translate-x-1/2">
              <div
                className="rounded-2xl border px-4 py-4"
                style={{
                  background: "rgba(16,15,14,0.92)",
                  borderColor: PATH.cardBorderLavender,
                  boxShadow: "0 12px 40px rgba(0,0,0,0.45)",
                }}
              >
                {mode === "learn" ? (
                  <>
                    <div className="mb-3 flex items-center justify-between text-xs text-white/50">
                      <span>
                        {isPlaying
                          ? `Nota ${seqIndex + 1}/${sequence.length}`
                          : learnDone
                            ? "Recorrido listo"
                            : "Escucha la caja POS I"}
                      </span>
                      <span className="flex items-center gap-1">
                        <Sparkles className="h-3.5 w-3.5 text-[#c4b5fd]" />
                        {runtime.positionLabel}
                      </span>
                    </div>
                    <div className="mb-3 flex gap-1">
                      {sequence.map((_, i) => (
                        <div
                          key={i}
                          className="h-1.5 flex-1 rounded-full transition-colors"
                          style={{
                            background:
                              i < seqIndex
                                ? "#99f6e4"
                                : i === seqIndex && isPlaying
                                  ? "#c4b5fd"
                                  : "rgba(255,255,255,0.1)",
                          }}
                        />
                      ))}
                    </div>
                    <button
                      type="button"
                      onClick={() => (isPlaying ? stopLearnPlayback() : startLearnPlayback())}
                      className="flex w-full items-center justify-center gap-2 rounded-xl py-3 text-sm font-medium text-[#100f0e]"
                      style={{ background: PATH.btnLavender }}
                    >
                      {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" fill="#100f0e" />}
                      {isPlaying ? "Pausar" : learnDone ? "Repetir escucha" : "Reproducir escala"}
                    </button>
                    {learnDone && (
                      <button
                        type="button"
                        onClick={() => void startPractice()}
                        className="mt-2 w-full rounded-xl border py-2.5 text-sm text-white/80 hover:bg-white/5"
                        style={{ borderColor: PATH.cardBorder }}
                      >
                        Pasar a Practicar con mic
                      </button>
                    )}
                  </>
                ) : (
                  <>
                    <div className="mb-3 flex items-center justify-between text-xs text-white/50">
                      <span>
                        {practiceActive
                          ? `Toca la nota ${seqIndex + 1}/${sequence.length}`
                          : "Micrófono listo"}
                      </span>
                      {pitchState.isListening && (
                        <span className="flex items-center gap-1 text-emerald-400/90">
                          <Mic className="h-3.5 w-3.5" /> Escuchando
                        </span>
                      )}
                    </div>
                    <div className="mb-3 flex gap-1">
                      {sequence.map((_, i) => (
                        <div
                          key={i}
                          className="h-1.5 flex-1 rounded-full"
                          style={{
                            background: i < seqIndex ? "#99f6e4" : i === seqIndex ? "#c4b5fd" : "rgba(255,255,255,0.1)",
                          }}
                        />
                      ))}
                    </div>
                    {!practiceActive ? (
                      <button
                        type="button"
                        onClick={() => void startPractice()}
                        className="flex w-full items-center justify-center gap-2 rounded-xl py-3 text-sm font-medium text-[#100f0e]"
                        style={{ background: "linear-gradient(135deg, #99f6e4, #7ecfc0)" }}
                      >
                        <Mic className="h-4 w-4" />
                        Empezar a tocar
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={stopPractice}
                        className="w-full rounded-xl border py-2.5 text-sm text-white/80"
                        style={{ borderColor: PATH.cardBorder }}
                      >
                        Detener
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </SongLearnCanvasBackdrop>
  );
}

export default ScalePathMissionStudio;
